import React, { useEffect, useState } from 'react';
import { Alert, Button } from 'reactstrap';

import api from '../utils/Api';
import { trackEvent } from '../utils/Analytics';
import { recoveryCodesText } from '../utils/TwoStep';
import JoinQrCode from './JoinQrCode';

import './TwoStepSettings.scss';

interface TwoStepSettingsProps {
  token: string;
  email: string;
  /** False for an account that signs in only with Google. */
  hasPassword: boolean;
  onNotice: (tone: 'ok' | 'error', text: string) => void;
}

interface Status {
  enabled: boolean;
  recoveryCodesLeft: number;
}

/** A phone or tablet, where the authenticator app is on this same device. */
const isTouchDevice = () =>
  typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia('(pointer: coarse)').matches;

type Panel = null | 'password' | 'scan' | 'codes' | 'turn-off' | 'new-codes';

/**
 * Two-step sign-in, in Edit Profile. Off: a link to turn it on, which asks for the password,
 * shows a QR code to scan into an authenticator app, and takes the first code it makes.
 * Then the ten recovery codes, shown once. On: how many recovery codes are left, a new set,
 * and turning it off (password and a code).
 */
const TwoStepSettings = ({ token, email, hasPassword, onNotice }: TwoStepSettingsProps) => {
  const [status, setStatus] = useState<Status | null>(null);
  const [panel, setPanel] = useState<Panel>(null);
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [setup, setSetup] = useState<{ secret: string; otpauthUri: string } | null>(null);
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // On a phone the app is right here, so the link that opens it comes first and the QR
  // code (for an app on another device) waits behind a link.
  const [onPhone] = useState(isTouchDevice);
  const [showQr, setShowQr] = useState(false);

  useEffect(() => {
    let live = true;
    api
      .users('mfa', 'GET', token, null)
      .then(response => live && setStatus({ enabled: !!response.enabled, recoveryCodesLeft: response.recoveryCodesLeft || 0 }))
      .catch(() => live && setStatus(null));
    return () => {
      live = false;
    };
  }, [token]);

  const close = () => {
    setPanel(null);
    setPassword('');
    setCode('');
    setSetup(null);
    setShowQr(false);
    setError('');
  };

  // Every step posts, shows the server's sentence on failure, and hands back the answer.
  const send = async (path: string, body: any) => {
    setBusy(true);
    setError('');
    try {
      return await api.users(path, 'POST', token, body);
    } catch (err) {
      setError(err.message);
      return null;
    } finally {
      setBusy(false);
    }
  };

  const startSetup = async () => {
    if (!password) {
      setError('Enter your password.');
      return;
    }
    const response = await send('mfa/setup', { password });
    if (response) {
      setSetup({ secret: response.secret, otpauthUri: response.otpauthUri });
      setPassword('');
      setPanel('scan');
    }
  };

  const confirmSetup = async () => {
    const response = await send('mfa/enable', { code });
    if (response) {
      trackEvent('two_step_enabled');
      setCode('');
      setSetup(null);
      setRecoveryCodes(response.recoveryCodes || []);
      setStatus({ enabled: true, recoveryCodesLeft: (response.recoveryCodes || []).length });
      setPanel('codes');
    }
  };

  const turnOff = async () => {
    const response = await send('mfa/disable', { password, code });
    if (response) {
      trackEvent('two_step_disabled');
      setStatus({ enabled: false, recoveryCodesLeft: 0 });
      close();
      onNotice('ok', 'Two-step sign-in is off.');
    }
  };

  const makeNewCodes = async () => {
    const response = await send('mfa/recovery-codes', { code });
    if (response) {
      trackEvent('two_step_codes_renewed');
      setCode('');
      setRecoveryCodes(response.recoveryCodes || []);
      setStatus({ enabled: true, recoveryCodesLeft: (response.recoveryCodes || []).length });
      setPanel('codes');
    }
  };

  const downloadCodes = () => {
    const blob = new Blob([recoveryCodesText(email, recoveryCodes)], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'jookable-recovery-codes.txt';
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const copyCodes = () => {
    if (navigator.clipboard) {
      navigator.clipboard
        .writeText(recoveryCodesText(email, recoveryCodes))
        .then(() => onNotice('ok', 'Recovery codes copied.'))
        .catch(() => onNotice('error', 'Could not copy. Download them instead.'));
    }
  };

  const codeInput = (label: string, allowRecovery: boolean) => (
    <input
      type="text"
      value={code}
      placeholder={allowRecovery ? '6-digit code or a recovery code' : '6-digit code'}
      aria-label={label}
      inputMode={allowRecovery ? 'text' : 'numeric'}
      autoComplete="one-time-code"
      autoCapitalize="none"
      spellCheck={false}
      maxLength={20}
      onChange={e => setCode(e.target.value)}
    />
  );

  if (!status) {
    return null;
  }

  return (
    <div className="two-step">
      <h3 className="two-step-title">
        Two-step sign-in
        <span className={`two-step-badge${status.enabled ? ' two-step-badge-on' : ''}`}>{status.enabled ? 'On' : 'Off'}</span>
      </h3>

      {panel === null && !status.enabled && (
        <>
          <p className="two-step-text">
            {hasPassword
              ? 'Ask for a code from an authenticator app on your phone, as well as your password, when you sign in.'
              : 'You sign in with Google, so Google’s own two-step settings protect your account. Add a password to use an authenticator app here as well.'}
          </p>
          {hasPassword && (
            <button type="button" className="delete-account-link two-step-link" onClick={() => setPanel('password')}>
              Turn on two-step sign-in
            </button>
          )}
        </>
      )}

      {panel === null && status.enabled && (
        <>
          <p className="two-step-text">
            Signing in asks for a code from your authenticator app. You have {status.recoveryCodesLeft} recovery{' '}
            {status.recoveryCodesLeft === 1 ? 'code' : 'codes'} left.
          </p>
          <div className="two-step-links">
            <button type="button" className="delete-account-link two-step-link" onClick={() => setPanel('new-codes')}>
              New recovery codes
            </button>
            <button type="button" className="delete-account-link" onClick={() => setPanel('turn-off')}>
              Turn off
            </button>
          </div>
        </>
      )}

      {panel !== null && (
        <div className="delete-account-panel">
          {panel === 'password' && (
            <>
              <p>You will need an authenticator app, such as Google Authenticator, Microsoft Authenticator or 1Password.</p>
              <input
                type="password"
                value={password}
                placeholder="Your password"
                aria-label="Your password"
                autoComplete="current-password"
                onChange={e => setPassword(e.target.value)}
              />
            </>
          )}

          {panel === 'scan' && setup && (
            <>
              {onPhone ? (
                <>
                  <p>Tap the button to add Jookable to your authenticator app. Then come back here and type the 6-digit code it shows.</p>
                  {/* otpauth:// links open Google Authenticator, Microsoft Authenticator, the iPhone's
                      own Passwords app and most others, with the key already filled in. */}
                  <a className="btn btn-primary two-step-open-app" href={setup.otpauthUri}>
                    Open authenticator app
                  </a>
                  {showQr ? (
                    <div className="two-step-qr">
                      <JoinQrCode url={setup.otpauthUri} />
                    </div>
                  ) : (
                    <button type="button" className="delete-account-link two-step-link" onClick={() => setShowQr(true)}>
                      Authenticator on another device? Show a QR code
                    </button>
                  )}
                </>
              ) : (
                <>
                  <p>Scan this with the authenticator app on your phone, then type the 6-digit code it shows.</p>
                  <div className="two-step-qr">
                    <JoinQrCode url={setup.otpauthUri} />
                  </div>
                </>
              )}
              <p className="two-step-secret">
                {onPhone ? 'Nothing opened? Add Jookable in the app by hand with this key:' : 'Can’t scan? Enter this key in the app instead:'}
                <code>{setup.secret.replace(/(.{4})/g, '$1 ').trim()}</code>
                {onPhone && navigator.clipboard && (
                  <button
                    type="button"
                    className="delete-account-link two-step-link"
                    onClick={() =>
                      navigator.clipboard
                        .writeText(setup.secret)
                        .then(() => onNotice('ok', 'Key copied.'))
                        .catch(() => onNotice('error', 'Could not copy the key.'))
                    }
                  >
                    Copy key
                  </button>
                )}
              </p>
              {codeInput('6-digit code', false)}
            </>
          )}

          {panel === 'codes' && (
            <>
              <p>
                <strong>Save these recovery codes.</strong> If you lose your phone, each one signs you in once. This is the
                only time they are shown.
              </p>
              <ol className="two-step-codes">
                {recoveryCodes.map(c => (
                  <li key={c}>
                    <code>{c}</code>
                  </li>
                ))}
              </ol>
            </>
          )}

          {panel === 'turn-off' && (
            <>
              <p>Turning it off means only your password is needed to sign in.</p>
              {hasPassword && (
                <input
                  type="password"
                  value={password}
                  placeholder="Your password"
                  aria-label="Your password"
                  autoComplete="current-password"
                  onChange={e => setPassword(e.target.value)}
                />
              )}
              {codeInput('Code from your app or a recovery code', true)}
            </>
          )}

          {panel === 'new-codes' && (
            <>
              <p>Your old recovery codes stop working once you make new ones.</p>
              {codeInput('Code from your app or a recovery code', true)}
            </>
          )}

          {error && <Alert color="danger">{error}</Alert>}

          <div className="delete-account-actions">
            {panel === 'codes' ? (
              <>
                <Button color="secondary" onClick={copyCodes}>
                  {'Copy'}
                </Button>
                <Button color="secondary" onClick={downloadCodes}>
                  {'Download'}
                </Button>
                <Button
                  color="primary"
                  onClick={() => {
                    setRecoveryCodes([]);
                    close();
                    onNotice('ok', 'Two-step sign-in is on.');
                  }}
                >
                  {'I saved them'}
                </Button>
              </>
            ) : (
              <>
                <Button color="secondary" disabled={busy} onClick={close}>
                  {'Cancel'}
                </Button>
                {panel === 'password' && (
                  <Button color="primary" disabled={busy} onClick={startSetup}>
                    {busy ? 'Checking…' : 'Next'}
                  </Button>
                )}
                {panel === 'scan' && (
                  <Button color="primary" disabled={busy} onClick={confirmSetup}>
                    {busy ? 'Checking…' : 'Turn on'}
                  </Button>
                )}
                {panel === 'turn-off' && (
                  <Button color="danger" disabled={busy} onClick={turnOff}>
                    {busy ? 'Saving…' : 'Turn off'}
                  </Button>
                )}
                {panel === 'new-codes' && (
                  <Button color="primary" disabled={busy} onClick={makeNewCodes}>
                    {busy ? 'Saving…' : 'Make new codes'}
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TwoStepSettings;
